import { ExternalServiceId, ExternalServiceIdProperty, InternalId, UrlProperty, Uuid, UuidProperty } from '@edgebox/data-definition-kit';
import { ProgressBar } from '../../Governance/Components/ProgressBar';
import { Alert, Button } from '@edgebox/react-components';
import {
  ContentEdit,
  IRuntimeMetadataFacetDiff,
  IRuntimeMetadataFacetGroup,
  TaskGroupType,
  TaskStatus,
} from '@edgebox/sync-core-data-definitions';
import { ClientContentItemEntity, ISyncCoreApi, ClientTaskGroupDraft } from '@edgebox/sync-core-rest-client';
import { plainToInstance } from 'class-transformer';
import React from 'react';
import { SyncCoreApiContext } from '../../../../contexts/SyncCoreApiContext';
import { IOptimizeHealth, IOptimizeHostReads, IOptimizeHostReady, IRenderOptimizeDiff, sendToParent } from '../../../../frame-messages';
import { ParamsComponent } from '../../ParamsComponent';
import { BP_CARD_CLASSES } from '../../Governance/Components/brandPresenceClasses';
import { ContentItemGapsTable } from '../../Governance/Components/ContentItemGapsTable';
import { CMS_KPI_CARD_ROW_CLASS_NAME, ContentItemKpiCards } from '../../Governance/Components/ContentItemKpiCards';
import { COUNT_ONLY } from '../../Governance/Components/GovernanceTermUsageCards';
import { findPageByUrl } from '../../Governance/Components/pageByUrl.client';
import { toComparableContents } from '../../Governance/Components/contentBlocks';
import {
  loadDraftOfOptimizedPage,
  loadDraftOfPage,
  loadOptimizationForSuggestion,
} from '../../Governance/Components/optimizationForSuggestion.client';
import {
  ISiteDraftNotice,
  REVIEWABLE_STATUSES,
  SITE_DRAFT_REVIEW_BODY_IN_SITE_COPY,
  toSiteDraftNotice,
} from '../../Governance/Components/siteDraftNotice';
import { SiteDraftReviewNotice } from '../../Governance/Components/SiteDraftReviewNotice';
import { IMetadataReviewDecisionSnapshot, MetadataReview } from './MetadataReview';

class EntityReference {
  @ExternalServiceIdProperty(false)
  namespaceMachineName?: ExternalServiceId;

  @ExternalServiceIdProperty(false)
  machineName?: ExternalServiceId;

  @UuidProperty(false)
  remoteUuid?: Uuid;

  @UuidProperty(false)
  sourceUuid?: Uuid;

  @ExternalServiceIdProperty(false)
  remoteUniqueId?: ExternalServiceId;
}

class Params extends EntityReference {
  @UrlProperty(true, false, { allowLocalhost: true })
  canonicalUrl!: string;
}

/** How long the screen waits between two reads of a suggested version Sync Core has not measured yet. */
const SUGGESTION_MEASURE_POLL_INTERVAL_MS = 5_000;

/** How often it reads before it leaves the suggested version unmeasured: five minutes, the measurement's own pace. */
const SUGGESTION_MEASURE_READS = 60;

/** Whether Sync Core has measured a page or suggested version: a measured one carries its checks. */
function isMeasured(row: ClientContentItemEntity | undefined): boolean {
  return !!row?.health?.length;
}

type TaskGroupSummaryItem = { status?: string; count?: number };
type CrawlProgress = { progress: number; title: string; finished: boolean };
type ApplyProgress = { progress: number; title: string; finished: boolean };
type OptimizeDiffGroup = { id: string; changeIds: string[]; sourceChangeIds?: string[]; canApprove?: boolean };
type ApprovedOptimizeAction = {
  sourceChangeIds?: string[];
  canApprove?: boolean;
};

function toNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

function getFileIdFromReference(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as { id?: string; getId?: () => string | undefined };
  return toNonEmptyString(candidate.getId?.()) || toNonEmptyString(candidate.id);
}

function getExtractionFileId(item: ClientContentItemEntity, extractionKeys: string[]): string | undefined {
  const itemAny = item as unknown as { extractions?: Record<string, unknown> };
  const extractions = itemAny.extractions;
  if (!extractions || typeof extractions !== 'object') {
    return undefined;
  }

  for (const key of extractionKeys) {
    const descriptor = extractions[key] as { file?: unknown } | undefined;
    const fileId = getFileIdFromReference(descriptor?.file);
    if (fileId) {
      return fileId;
    }
  }

  return undefined;
}

async function loadExtractionFileContent(api: ISyncCoreApi, fileId: string): Promise<string | undefined> {
  const file = await api.service.files.item(fileId as InternalId);
  if (!file.downloadUrl) {
    return undefined;
  }
  const response = await fetch(file.downloadUrl);

  if (!response.ok) {
    return undefined;
  }

  return response.text();
}

function toInternalId(value: unknown): InternalId | undefined {
  const asString = toNonEmptyString(typeof value === 'string' ? value : undefined);
  if (asString) {
    return asString as InternalId;
  }

  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as { id?: string; getId?: () => string | undefined };
  const runtime = toNonEmptyString(candidate.getId?.());
  if (runtime) {
    return runtime as InternalId;
  }

  const plain = toNonEmptyString(candidate.id);
  return plain ? (plain as InternalId) : undefined;
}

function toApplyProgress(status: TaskStatus): ApplyProgress {
  if (status === TaskStatus.Finished) {
    return { progress: 100, title: 'Optimization updates applied.', finished: true };
  }

  if (status === TaskStatus.Running || status === TaskStatus.Retrying) {
    return { progress: 66, title: 'Applying optimization updates…', finished: false };
  }

  if (status === TaskStatus.Initializing) {
    return { progress: 24, title: 'Preparing optimization update task…', finished: false };
  }

  return { progress: 92, title: `Applying optimization updates (${status})…`, finished: false };
}

function parseContentEdit(rawDelta: string | undefined): ContentEdit | undefined {
  if (!rawDelta) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(rawDelta) as unknown;
    return plainToInstance(ContentEdit, parsed);
  } catch {
    return undefined;
  }
}

/** The stored delta of a draft's revision: the draft as rendered and, for a draft Sync Core generated, its marked changes. */
async function loadContentEdit(api: ISyncCoreApi, suggestionItem: ClientContentItemEntity): Promise<ContentEdit | undefined> {
  const contentSnapshotDeltaFileId = getExtractionFileId(suggestionItem, ['content_snapshot_delta', 'contentSnapshotDelta']);
  const rawDelta = contentSnapshotDeltaFileId ? await loadExtractionFileContent(api, contentSnapshotDeltaFileId) : undefined;
  return parseContentEdit(rawDelta);
}

/**
 * Posts a comparison, or its withdrawal, to the page hosting this screen, and keeps it as the latest: a
 * host that starts listening after a comparison was posted missed it, and is sent it again when it says
 * so. A withdrawal is kept too, so that a comparison it took back is never sent again.
 */
function postComparison(latest: React.MutableRefObject<IRenderOptimizeDiff | undefined>, message: IRenderOptimizeDiff) {
  latest.current = message;
  sendToParent(message);
}

/** A health figure as it goes to the host: the stored figure where it is one, nothing where there is none. */
function toHealthFigure(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Whether a posted message shows something on the host, rather than taking a comparison off it. */
function isComparison(message: IRenderOptimizeDiff): boolean {
  return !!(message.sourceHtml || message.targetHtml || message.snapshotHtml);
}

/** Takes a comparison this screen posted off the host again, with the message every host reads as "hide". */
function withdrawComparison(shown: React.MutableRefObject<boolean>, latest: React.MutableRefObject<IRenderOptimizeDiff | undefined>) {
  if (!shown.current) {
    return;
  }

  shown.current = false;
  postComparison(latest, { type: 'render-optimize-diff', sourceHtml: null, targetHtml: null });
}

function buildChangeGroups(contentEdit: ContentEdit | undefined): OptimizeDiffGroup[] {
  if (!contentEdit) {
    return [];
  }

  const itemBySourceChangeId = new Map<string, ContentEdit['items'][number]>();
  (contentEdit.items || []).forEach((item) => {
    const sourceId = toNonEmptyString(item.sourceChangeId);
    if (sourceId) {
      itemBySourceChangeId.set(sourceId, item);
    }
  });

  const groupById = new Map<string, { changeIds: Set<string>; sourceChangeIds: Set<string> }>();
  const mappedGroupIds = new Set<string>();

  const ensureGroup = (groupId: string) => {
    const existing = groupById.get(groupId);
    if (existing) {
      return existing;
    }

    const created = { changeIds: new Set<string>(), sourceChangeIds: new Set<string>() };
    groupById.set(groupId, created);
    return created;
  };

  (contentEdit.changeMappings || []).forEach((mapping) => {
    const sourceChangeId = toNonEmptyString(mapping.sourceChangeId);
    const targetChangeId = toNonEmptyString(mapping.targetChangeId);
    if (!sourceChangeId || !targetChangeId) {
      return;
    }

    const item = itemBySourceChangeId.get(sourceChangeId);
    const groupId = toNonEmptyString(item?.entityChangeId) || sourceChangeId;
    const group = ensureGroup(groupId);
    group.changeIds.add(targetChangeId);
    group.sourceChangeIds.add(sourceChangeId);
    mappedGroupIds.add(groupId);
  });

  (contentEdit.targetChanges || []).forEach((targetChange) => {
    const targetChangeId = toNonEmptyString(targetChange.id);
    if (!targetChangeId) {
      return;
    }

    const exists = Array.from(groupById.values()).some((group) => group.changeIds.has(targetChangeId));
    if (exists) {
      return;
    }

    const group = ensureGroup(targetChangeId);
    group.changeIds.add(targetChangeId);
  });

  return Array.from(groupById.entries())
    .map(([id, group]) => ({
      id,
      changeIds: Array.from(group.changeIds),
      sourceChangeIds: group.sourceChangeIds.size ? Array.from(group.sourceChangeIds) : undefined,
      canApprove: mappedGroupIds.has(id),
    }))
    .filter((group) => group.changeIds.length > 0);
}

function inferCrawlProgress(summary: TaskGroupSummaryItem[] | undefined): CrawlProgress {
  if (!summary?.length) {
    return {
      progress: 8,
      title: 'Crawl in progress',
      finished: false,
    };
  }

  const total = summary.reduce((sum, item) => sum + (item.count || 0), 0);

  if (total <= 0) {
    return {
      progress: 8,
      title: 'Crawl in progress',
      finished: false,
    };
  }

  const doneStatusKeywords = ['finished', 'failed', 'aborted', 'skipped', 'limitexceeded', 'limit_exceeded'];
  const doneCount = summary.reduce((sum, item) => {
    const normalized = String(item.status || '').toLowerCase();
    const done = doneStatusKeywords.some((keyword) => normalized.includes(keyword));
    return sum + (done ? item.count || 0 : 0);
  }, 0);

  const rawProgress = Math.round((doneCount / total) * 100);
  const finished = doneCount >= total;
  const progress = finished ? 100 : Math.max(8, Math.min(98, rawProgress));

  return {
    progress,
    title: `Crawl in progress: ${doneCount} of ${total} items processed`,
    finished,
  };
}

interface IProps {
  params: Params;
}

function OptimizeWithParams({ params }: IProps) {
  const apiContext = React.useContext(SyncCoreApiContext);
  const api = apiContext?.api;

  const [loading, setLoading] = React.useState(true);
  const [item, setItem] = React.useState<ClientContentItemEntity | undefined>(undefined);
  const [lookupItemCount, setLookupItemCount] = React.useState<number | undefined>(undefined);
  const [loadError, setLoadError] = React.useState<string | undefined>(undefined);
  const [crawlTaskGroupId, setCrawlTaskGroupId] = React.useState<InternalId | undefined>(undefined);
  const [crawlProgress, setCrawlProgress] = React.useState<CrawlProgress | undefined>(undefined);
  const [crawlSubmitting, setCrawlSubmitting] = React.useState(false);
  const [crawlError, setCrawlError] = React.useState<string | undefined>(undefined);
  const [averageQualityScore, setAverageQualityScore] = React.useState<number | undefined>(undefined);
  const [hasOptimizationSuggestion, setHasOptimizationSuggestion] = React.useState(false);
  const [suggestionLoading, setSuggestionLoading] = React.useState(false);
  const [suggestionError, setSuggestionError] = React.useState<string | undefined>(undefined);
  const [applyInfo, setApplyInfo] = React.useState<string | undefined>(undefined);
  const [optimizationId, setOptimizationId] = React.useState<InternalId | undefined>(undefined);
  const [suggestionAuthoredExternally, setSuggestionAuthoredExternally] = React.useState<boolean>(false);
  // The suggested version under review, whose measured figures the checks and the health show beside the page's.
  const [draftItem, setDraftItem] = React.useState<ClientContentItemEntity | undefined>(undefined);
  // The site's word on a draft it wrote, once that draft has left the review.
  const [siteDraftOutcome, setSiteDraftOutcome] = React.useState<ISiteDraftNotice | undefined>(undefined);
  // The page whose draft lookup has answered. Until it has, the screen does not know whether it shows a
  // review or the gaps, and draws neither: the gaps table reads its own rows the moment it mounts.
  const [suggestionCheckedForItemId, setSuggestionCheckedForItemId] = React.useState<string | undefined>(undefined);
  // Asks the lookup again, once an optimization started from the gaps has its draft.
  const [suggestionReload, setSuggestionReload] = React.useState(0);
  const reloadSuggestion = React.useCallback(() => setSuggestionReload((count) => count + 1), []);
  // Whether the host shows a comparison this screen posted, so a lookup that no longer offers a draft
  // takes it off the host rather than leaving a draft on screen that has left the review.
  const comparisonShownRef = React.useRef(false);
  // The latest comparison, or withdrawal, posted to the host, for a host that says it listens only now.
  const latestComparisonRef = React.useRef<IRenderOptimizeDiff | undefined>(undefined);
  // The page's health now and in the suggested version, as last worked out for the host beside its comparison.
  const latestHealthRef = React.useRef<IOptimizeHealth | undefined>(undefined);
  // Whether the host said it reads that health: only then is it sent, since a host released before it answers a
  // message it does not know with an error.
  const hostReadsHealthRef = React.useRef(false);
  const [entityChangeIdBySourceChangeId, setEntityChangeIdBySourceChangeId] = React.useState<Record<string, string>>({});
  const [applyTaskId, setApplyTaskId] = React.useState<InternalId | undefined>(undefined);
  const [applyProgress, setApplyProgress] = React.useState<ApplyProgress | undefined>(undefined);
  const [applyError, setApplyError] = React.useState<string | undefined>(undefined);
  const [metadataChanges, setMetadataChanges] = React.useState<IRuntimeMetadataFacetDiff[]>([]);
  const [metadataChangeGroups, setMetadataChangeGroups] = React.useState<IRuntimeMetadataFacetGroup[] | undefined>(undefined);
  const metadataDecisionsRef = React.useRef<IMetadataReviewDecisionSnapshot>({ approvedSourceChangeIds: [], totalApprovableCount: 0 });
  const handleMetadataDecisionsChange = React.useCallback((snapshot: IMetadataReviewDecisionSnapshot) => {
    metadataDecisionsRef.current = snapshot;
  }, []);

  const canonicalUrl = String(params.canonicalUrl || '').trim();

  const loadContentItem = React.useCallback(async () => {
    if (!api || !canonicalUrl) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(undefined);
    setLookupItemCount(undefined);

    try {
      const { page, candidateCount } = await findPageByUrl(api, canonicalUrl);
      setLookupItemCount(candidateCount);
      setItem(page);
    } catch (error) {
      setLoadError((error as Error)?.message || 'Failed to look up content item.');
      setLookupItemCount(undefined);
      setItem(undefined);
    } finally {
      setLoading(false);
    }
  }, [api, canonicalUrl]);

  React.useEffect(() => {
    void loadContentItem();
  }, [loadContentItem]);

  React.useEffect(() => {
    const onParentMessage = (event: Event) => {
      const message = (event as CustomEvent).detail as {
        type?: string;
        approvedCount?: number;
        totalChanges?: number;
        approvedActions?: ApprovedOptimizeAction[];
      };

      if (message?.type !== 'apply-optimize-changes') {
        return;
      }

      const approvedCount = typeof message.approvedCount === 'number' ? message.approvedCount : 0;
      const totalChanges = typeof message.totalChanges === 'number' ? message.totalChanges : 0;

      void (async () => {
        if (!api) {
          setApplyError('Sync Core API is not available.');
          return;
        }

        if (!optimizationId) {
          setApplyError('Unable to apply changes: optimization context is missing.');
          return;
        }

        const approvedSourceChangeIds = new Set<string>();
        (message.approvedActions || []).forEach((action) => {
          if (action?.canApprove === false) {
            return;
          }

          (action?.sourceChangeIds || []).forEach((sourceChangeId) => {
            const normalized = toNonEmptyString(sourceChangeId);
            if (normalized) {
              approvedSourceChangeIds.add(normalized);
            }
          });
        });

        metadataDecisionsRef.current.approvedSourceChangeIds.forEach((sourceChangeId) => {
          const normalized = toNonEmptyString(sourceChangeId);
          if (normalized) {
            approvedSourceChangeIds.add(normalized);
          }
        });

        const approvedEntityChangeIds = Array.from(approvedSourceChangeIds)
          .map((sourceChangeId) => entityChangeIdBySourceChangeId[sourceChangeId])
          .filter((entityChangeId): entityChangeId is string => !!entityChangeId);

        setApplyError(undefined);
        setApplyInfo(
          approvedCount > 0
            ? `Optimization is being applied (${approvedCount} approved out of ${totalChanges || approvedCount} reviewed changes).`
            : 'Applying review decision with no approved changes.'
        );
        setApplyProgress({ progress: 16, title: 'Starting optimization update…', finished: false });

        try {
          const applyResult = await api.governance.contentOptimizations.applyChanges(optimizationId, {
            approvedEntityChangeIds,
          });

          const nextTaskId = toInternalId(applyResult.crawlTask?.id);
          if (nextTaskId) {
            setApplyTaskId(nextTaskId);
          } else {
            setApplyProgress({ progress: 100, title: 'Optimization updates applied.', finished: true });
          }

          comparisonShownRef.current = false;
          postComparison(latestComparisonRef, {
            type: 'render-optimize-diff',
            sourceHtml: null,
            targetHtml: null,
          });
          setMetadataChanges([]);
          setMetadataChangeGroups(undefined);
          metadataDecisionsRef.current = { approvedSourceChangeIds: [], totalApprovableCount: 0 };
        } catch (error) {
          setApplyError((error as Error)?.message || 'Failed to apply optimization changes.');
          setApplyProgress(undefined);
        }
      })();
    };

    window.addEventListener('content-sync-parent-message', onParentMessage);
    return () => {
      window.removeEventListener('content-sync-parent-message', onParentMessage);
    };
  }, [api, optimizationId, entityChangeIdBySourceChangeId]);

  // A host says once that it listens for the comparison. When its own scripts ran after this screen had
  // posted it, the comparison reached a page that was not listening yet, so it is posted again, once. A
  // withdrawal is not: a host that missed it shows nothing to take off. Before this screen has posted
  // anything, there is nothing to post again.
  React.useEffect(() => {
    const onHostReady = (event: Event) => {
      const message = (event as CustomEvent).detail as Partial<IOptimizeHostReady> | undefined;
      const latest = latestComparisonRef.current;
      if (message?.type !== 'optimize-host-ready' || !latest || !isComparison(latest)) {
        return;
      }

      latestComparisonRef.current = undefined;
      sendToParent(latest);
      if (latestHealthRef.current && hostReadsHealthRef.current) {
        sendToParent(latestHealthRef.current);
      }
    };

    window.addEventListener('content-sync-parent-message', onHostReady);
    return () => {
      window.removeEventListener('content-sync-parent-message', onHostReady);
    };
  }, []);

  // A host names the messages beyond the comparison it reads, once it listens and with each comparison it is sent.
  // The page's health goes only to a host that named it, and goes at once where it was worked out before the host
  // said so.
  React.useEffect(() => {
    const onHostReads = (event: Event) => {
      const message = (event as CustomEvent).detail as Partial<IOptimizeHostReads> | undefined;
      if (message?.type !== 'optimize-host-reads' || !Array.isArray(message.reads) || !message.reads.includes('optimize-health')) {
        return;
      }

      hostReadsHealthRef.current = true;
      if (latestHealthRef.current) {
        sendToParent(latestHealthRef.current);
      }
    };

    window.addEventListener('content-sync-parent-message', onHostReads);
    return () => {
      window.removeEventListener('content-sync-parent-message', onHostReads);
    };
  }, []);

  React.useEffect(() => {
    setApplyInfo(undefined);
    setApplyError(undefined);
    setApplyProgress(undefined);
    setApplyTaskId(undefined);
    setOptimizationId(undefined);
    // Another page is not compared with this page's suggested version, not even while its own lookup runs.
    setDraftItem(undefined);
    setEntityChangeIdBySourceChangeId({});
    setMetadataChanges([]);
    setMetadataChangeGroups(undefined);
    metadataDecisionsRef.current = { approvedSourceChangeIds: [], totalApprovableCount: 0 };
  }, [item?.id]);

  React.useEffect(() => {
    if (!api || !item || !item.key) {
      setHasOptimizationSuggestion(false);
      setDraftItem(undefined);
      setSuggestionAuthoredExternally(false);
      setSiteDraftOutcome(undefined);
      setSuggestionLoading(false);
      setSuggestionError(undefined);
      setSuggestionCheckedForItemId(item?.id);
      return;
    }

    let active = true;
    const itemId = item.id;
    const settle = () => {
      setSuggestionLoading(false);
      setSuggestionCheckedForItemId(itemId);
    };

    void (async () => {
      setSuggestionLoading(true);
      setSuggestionError(undefined);

      try {
        const suggestionItem = await loadDraftOfOptimizedPage(api, { pageKey: item.key });
        if (!active) {
          return;
        }

        if (!suggestionItem) {
          setHasOptimizationSuggestion(false);
          setDraftItem(undefined);
          setSuggestionAuthoredExternally(false);
          setSiteDraftOutcome(undefined);
          setOptimizationId(undefined);
          setEntityChangeIdBySourceChangeId({});
          settle();
          withdrawComparison(comparisonShownRef, latestComparisonRef);
          return;
        }

        const suggestionRevisionId = toInternalId(suggestionItem.id);
        const sourceRevisionId = toInternalId(item.id);
        const optimization = suggestionRevisionId
          ? await loadOptimizationForSuggestion(api, { suggestionRevisionId, pageKey: item.key })
          : undefined;
        if (!active) {
          return;
        }

        // Only a draft that awaits the person's review is offered. A draft still being written, one whose
        // generation failed, one already approved or live: none of them has anything to decide here, and
        // the gaps are what the page still has to fix.
        if (!optimization || !REVIEWABLE_STATUSES.includes(optimization.status)) {
          setHasOptimizationSuggestion(false);
          setDraftItem(undefined);
          setSuggestionAuthoredExternally(false);
          setSiteDraftOutcome(toSiteDraftNotice(optimization));
          setOptimizationId(undefined);
          setEntityChangeIdBySourceChangeId({});
          settle();
          withdrawComparison(comparisonShownRef, latestComparisonRef);
          return;
        }

        // A draft the site wrote is a revision of the page on the site: it carries no marked changes, so it
        // is compared with the live page, and the host shows the comparison read only, because the site
        // publishes the draft with its own publishing. The page it is compared with is the published page
        // the draft was made from, not the live row, which a later crawl may already have replaced. The
        // crawl reads that page as visitors are shown it, while the site renders its draft in wrappers of
        // its own, so both content areas are read through the one routine that meets them block by
        // block: what differs between them is then what the draft changed.
        const draftSourceRevisionId = toInternalId(optimization.inputPage) || sourceRevisionId;
        if (optimization.externalRevisionId && draftSourceRevisionId && suggestionRevisionId) {
          const [sourceContentArea, targetContentArea] = await Promise.all([
            api.governance.contentItems.htmlContent(draftSourceRevisionId),
            api.governance.contentItems.htmlContent(suggestionRevisionId),
          ]);
          if (!active) {
            return;
          }
          const { sourceHtml, targetHtml } = toComparableContents({ sourceHtml: sourceContentArea, targetHtml: targetContentArea });

          setHasOptimizationSuggestion(true);
          setDraftItem(suggestionItem);
          setOptimizationId(toInternalId(optimization.id));
          setSuggestionAuthoredExternally(true);
          setSiteDraftOutcome(undefined);
          setEntityChangeIdBySourceChangeId({});
          setMetadataChanges([]);
          setMetadataChangeGroups(undefined);
          metadataDecisionsRef.current = { approvedSourceChangeIds: [], totalApprovableCount: 0 };
          settle();

          // The same blocks on both sides mean the service holds no page of the draft's own, only the page
          // it was made from, or a draft that changes nothing but the wrappers around every block: there is
          // nothing to compare, and the notice says the draft awaits its review and publishing, which the site
          // shows itself.
          if (sourceHtml === targetHtml) {
            withdrawComparison(comparisonShownRef, latestComparisonRef);
            return;
          }

          comparisonShownRef.current = true;
          postComparison(latestComparisonRef, {
            type: 'render-optimize-diff',
            sourceHtml,
            targetHtml,
            snapshotHtml: null,
            authoredExternally: true,
          });
          return;
        }

        const contentEdit = await loadContentEdit(api, suggestionItem);
        const snapshotHtml = toNonEmptyString(contentEdit?.annotatedTargetContentAreaHtml);
        const changeGroups = buildChangeGroups(contentEdit);
        const nextMetadataChanges: IRuntimeMetadataFacetDiff[] = (contentEdit?.metadataChanges || []).filter(
          (facet): facet is IRuntimeMetadataFacetDiff =>
            !!facet && typeof facet.sourceChangeId === 'string' && facet.sourceChangeId.length > 0
        );
        const nextMetadataChangeGroups: IRuntimeMetadataFacetGroup[] | undefined = contentEdit?.metadataChangeGroups?.length
          ? (contentEdit.metadataChangeGroups as IRuntimeMetadataFacetGroup[])
          : undefined;
        const nextMapping: Record<string, string> = {};
        (contentEdit?.items || []).forEach((changeItem) => {
          const sourceChangeId = toNonEmptyString(changeItem.sourceChangeId);
          const entityChangeId = toNonEmptyString(changeItem.entityChangeId);
          if (sourceChangeId && entityChangeId) {
            nextMapping[sourceChangeId] = entityChangeId;
          }
        });
        nextMetadataChanges.forEach((facet) => {
          const sourceChangeId = toNonEmptyString(facet.sourceChangeId);
          const entityChangeId = toNonEmptyString(facet.entityChangeId);
          if (sourceChangeId && entityChangeId) {
            nextMapping[sourceChangeId] = entityChangeId;
          }
        });

        if (!active) {
          return;
        }

        const hasMetadataChanges = nextMetadataChanges.length > 0;
        if (!snapshotHtml && !hasMetadataChanges) {
          setHasOptimizationSuggestion(false);
          setDraftItem(undefined);
          setSuggestionAuthoredExternally(false);
          setSiteDraftOutcome(undefined);
          settle();
          withdrawComparison(comparisonShownRef, latestComparisonRef);
          return;
        }

        setHasOptimizationSuggestion(true);
        setDraftItem(suggestionItem);
        setOptimizationId(toInternalId(optimization.id));
        setSuggestionAuthoredExternally(false);
        setSiteDraftOutcome(undefined);
        settle();
        setEntityChangeIdBySourceChangeId(nextMapping);
        setMetadataChanges(nextMetadataChanges);
        setMetadataChangeGroups(nextMetadataChangeGroups);
        metadataDecisionsRef.current = { approvedSourceChangeIds: [], totalApprovableCount: 0 };
        comparisonShownRef.current = true;
        postComparison(latestComparisonRef, {
          type: 'render-optimize-diff',
          sourceHtml: null,
          targetHtml: null,
          snapshotHtml: snapshotHtml || null,
          changeGroups: changeGroups.length ? changeGroups : undefined,
          metadataChanges: hasMetadataChanges ? nextMetadataChanges : undefined,
          metadataChangeGroups: nextMetadataChangeGroups,
        });
      } catch (error) {
        if (!active) {
          return;
        }

        setHasOptimizationSuggestion(false);
        setDraftItem(undefined);
        setSuggestionAuthoredExternally(false);
        setSiteDraftOutcome(undefined);
        settle();
        withdrawComparison(comparisonShownRef, latestComparisonRef);
        setSuggestionError((error as Error)?.message || 'Failed to load optimization suggestion.');
      }
    })();

    return () => {
      active = false;
    };
  }, [api, item, suggestionReload]);

  // A suggested version the site wrote arrives before Sync Core has measured it, and Sync Core measures it in the
  // minutes after: its row is read again until it carries its figures, so they show without a reload.
  // Only a draft the site wrote is measured after it is offered: one Sync Core wrote was measured before it passed its
  // own review. A draft whose changes are being applied is never read again: applying retires it.
  const draftMeasured = isMeasured(draftItem);
  const awaitsMeasurement = !!draftItem && !draftMeasured && hasOptimizationSuggestion && suggestionAuthoredExternally && !applyProgress;
  React.useEffect(() => {
    if (!api || !item?.key || !draftItem || !awaitsMeasurement) {
      return;
    }

    let active = true;
    let reads = 0;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const pageKey = item.key;
    const draftId = draftItem.id;

    const reread = async () => {
      reads += 1;
      try {
        const row = await loadDraftOfPage(api, { pageKey });
        if (!active) {
          return;
        }

        // Another suggested version took its place: the review is looked up again, with its own figures.
        if (!row || row.id !== draftId) {
          reloadSuggestion();
          return;
        }

        if (isMeasured(row)) {
          setDraftItem(row);
          return;
        }
      } catch {
        // A read that fails leaves the figures as they are; the next one may answer.
      }

      if (active && reads < SUGGESTION_MEASURE_READS) {
        timeout = setTimeout(() => {
          void reread();
        }, SUGGESTION_MEASURE_POLL_INTERVAL_MS);
      }
    };

    timeout = setTimeout(() => {
      void reread();
    }, SUGGESTION_MEASURE_POLL_INTERVAL_MS);

    return () => {
      active = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [api, item?.key, draftItem, awaitsMeasurement, reloadSuggestion]);

  React.useEffect(() => {
    if (!api || !applyTaskId) {
      return;
    }

    let active = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const refresh = async () => {
      try {
        const task = await api.service.tasks.item(applyTaskId, true);
        if (!active) {
          return;
        }

        const status = task.status as TaskStatus;
        if (status === TaskStatus.Finished) {
          setApplyProgress(toApplyProgress(status));
          setApplyTaskId(undefined);
          await loadContentItem();
          return;
        }

        if (status === TaskStatus.Failed || status === TaskStatus.Aborted || status === TaskStatus.LimitExceeded) {
          setApplyError(`Optimization apply task ended with status ${status}.`);
          setApplyTaskId(undefined);
          setApplyProgress(undefined);
          return;
        }

        setApplyProgress(toApplyProgress(status));
        timeout = setTimeout(() => {
          void refresh();
        }, 2_000);
      } catch (error) {
        if (!active) {
          return;
        }

        setApplyError((error as Error)?.message || 'Failed to refresh optimization apply task status.');
        setApplyTaskId(undefined);
        setApplyProgress(undefined);
      }
    };

    void refresh();

    return () => {
      active = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [api, applyTaskId, loadContentItem]);

  React.useEffect(() => {
    if (!api || !crawlTaskGroupId) {
      return;
    }

    let active = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const refresh = async () => {
      try {
        const summary = await api.service.taskGroups.summary(crawlTaskGroupId);
        if (!active) {
          return;
        }

        const nextProgress = inferCrawlProgress((summary.byStatus || []) as TaskGroupSummaryItem[]);
        if (nextProgress.finished) {
          setCrawlProgress(undefined);
          setCrawlTaskGroupId(undefined);
          await loadContentItem();
          return;
        }

        setCrawlProgress(nextProgress);
        timeout = setTimeout(() => {
          void refresh();
        }, 2_000);
      } catch (error) {
        if (!active) {
          return;
        }

        setCrawlError((error as Error)?.message || 'Failed to refresh crawl progress.');
        setCrawlTaskGroupId(undefined);
        setCrawlProgress(undefined);
      }
    };

    void refresh();

    return () => {
      active = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [api, crawlTaskGroupId, loadContentItem]);

  React.useEffect(() => {
    if (!api) {
      setAverageQualityScore(undefined);
      return;
    }

    let active = true;

    void (async () => {
      try {
        // The average rides in the list's envelope, so one row is asked for: the content list refuses a
        // page of none.
        const summary = await api.governance.contentItems.list(COUNT_ONLY);
        if (!active) {
          return;
        }

        setAverageQualityScore(summary.averageHealthScore === null ? undefined : summary.averageHealthScore);
      } catch {
        if (!active) {
          return;
        }

        setAverageQualityScore(undefined);
      }
    })();

    return () => {
      active = false;
    };
  }, [api]);

  // The suggested version the figures are compared with: the one of the page shown, once its lookup has answered.
  const draftOfThisPage = hasOptimizationSuggestion && !!item && suggestionCheckedForItemId === item.id ? draftItem : undefined;
  const currentHealth = toHealthFigure(item?.healthScore);
  const suggestedHealth = draftOfThisPage ? toHealthFigure(draftOfThisPage.healthScore) : undefined;

  // A host that reads it keeps the page's health now and in the suggested version beside the comparison it draws,
  // so the reader sees how it changes while reviewing; it is sent again whenever either figure changes, as when Sync
  // Core has measured a suggested version the site wrote.
  React.useEffect(() => {
    if (!draftOfThisPage) {
      latestHealthRef.current = undefined;
      return;
    }

    const message: IOptimizeHealth = { type: 'optimize-health', current: currentHealth, suggested: suggestedHealth };
    latestHealthRef.current = message;
    if (hostReadsHealthRef.current) {
      sendToParent(message);
    }
  }, [draftOfThisPage, currentHealth, suggestedHealth]);

  if (!canonicalUrl) {
    return <Alert variant="danger">Missing canonical URL.</Alert>;
  }

  const optimizationLoading = loading || (!!item && (suggestionLoading || suggestionCheckedForItemId !== item.id));

  // The screen is the page detail's own figures and checks, laid out on the App's page padding: every
  // region stands apart by the same gap, which the regions leave to the column rather than carrying a
  // margin of their own, since a margin utility would outweigh the gap.
  return (
    <div className="flex flex-col gap-6 px-6 py-8 text-slate-900">
      {optimizationLoading ? (
        <div>
          <div className="w-full h-1 bg-slate-200" title="Loading content optimization">
            <div className="h-full bg-primary animate-pulse" style={{ width: '100%' }} />
          </div>
          <div className="text-muted mt-2">Loading content optimization…</div>
        </div>
      ) : null}
      {loadError ? <Alert variant="danger">{loadError}</Alert> : null}

      {!loading && !loadError && item ? (
        // The tab is a CMS screen, framed as wide as the site's admin page: on a phone its figures stand one
        // per row, so no figure's name wraps.
        <ContentItemKpiCards
          api={api}
          item={item}
          averageQualityScore={averageQualityScore}
          rowClassName={CMS_KPI_CARD_ROW_CLASS_NAME}
          draft={draftOfThisPage}
        />
      ) : null}

      {!optimizationLoading && !loadError && !item ? <Alert variant="info">This content has not been crawled yet.</Alert> : null}

      {suggestionError ? <Alert variant="danger">{suggestionError}</Alert> : null}
      {applyInfo ? <Alert variant="info">{applyInfo}</Alert> : null}
      {applyError ? <Alert variant="danger">{applyError}</Alert> : null}
      {!optimizationLoading && !loadError && item && hasOptimizationSuggestion ? (
        suggestionAuthoredExternally ? (
          // The site publishes its own draft, with the step to publish it that the site shows below this screen.
          <SiteDraftReviewNotice body={SITE_DRAFT_REVIEW_BODY_IN_SITE_COPY} />
        ) : (
          <Alert variant="success">An optimized content version is available. Review and approve changes below the embed.</Alert>
        )
      ) : null}

      {!optimizationLoading && !loadError && item && !hasOptimizationSuggestion && siteDraftOutcome && !siteDraftOutcome.inReview ? (
        <Alert variant={siteDraftOutcome.live ? 'success' : 'info'} data-testid="optimize-site-outcome">
          {siteDraftOutcome.message}
        </Alert>
      ) : null}

      {!optimizationLoading && !loadError && item && hasOptimizationSuggestion && metadataChanges.length ? (
        <MetadataReview
          metadataChanges={metadataChanges}
          metadataChangeGroups={metadataChangeGroups}
          onDecisionsChange={handleMetadataDecisionsChange}
        />
      ) : null}

      {applyProgress ? (
        <div>
          <ProgressBar size="hairline" percent={applyProgress.progress} title={applyProgress.title} label={applyProgress.title} />
        </div>
      ) : null}

      {crawlError ? <Alert variant="danger">{crawlError}</Alert> : null}

      {crawlProgress ? (
        <div>
          <ProgressBar size="hairline" percent={crawlProgress.progress} title={crawlProgress.title} label={crawlProgress.title} />
        </div>
      ) : null}

      {!item && lookupItemCount === 0 ? (
        // In the screen's column the button keeps its own width rather than the column's.
        <div>
          <Button
            variant="primary"
            disabled={crawlSubmitting || !!crawlTaskGroupId}
            onClick={async () => {
              if (!api) {
                setCrawlError('Sync Core API is not available.');
                return;
              }

              setCrawlSubmitting(true);
              setCrawlError(undefined);
              setCrawlProgress(undefined);

              try {
                const draft = new ClientTaskGroupDraft();
                draft.type = TaskGroupType.Crawl;
                draft.urls = [canonicalUrl];

                const taskGroup = await api.service.taskGroups.create(draft);
                setCrawlTaskGroupId(taskGroup.id as InternalId);
              } catch (error) {
                setCrawlError((error as Error)?.message || 'Unable to start crawling.');
              } finally {
                setCrawlSubmitting(false);
              }
            }}
          >
            {crawlSubmitting ? 'Starting crawl...' : 'Start crawling'}
          </Button>
        </div>
      ) : null}

      {/* The checks stay whatever the page's optimization stands at: while a suggested version awaits its review they
          show how each check reads in it beside how it reads now. */}
      {!optimizationLoading && !loadError && item ? (
        <section className={BP_CARD_CLASSES}>
          <ContentItemGapsTable api={api} item={item} onDraftReady={reloadSuggestion} draft={draftOfThisPage} />
        </section>
      ) : null}
    </div>
  );
}

export const Optimize = () => (
  <ParamsComponent<Params> Params={Params}>
    {(params: Params) => {
      return <OptimizeWithParams params={params} />;
    }}
  </ParamsComponent>
);
